import omit from 'lodash/omit';
import type { IUserPlanPreferences } from '@creditflowresearch/user-plan/vo/IUserPlanResponse';
import { ALL_SEGMENT_IDS, SegmentTypeById } from '@/creditflowresearch/domain/Segment/constants';
import {
  EMPTY_MARKET_SEGMENT_SELECTION,
  IMarketSegmentSelection,
} from '@creditflowresearch/user-plan/vo/IMarketSegmentSelection';
import {
  getDefaultSegmentIds,
  getSegmentScopes,
  getVisibleSegmentIds,
  ISegmentScope,
} from '@/creditflowresearch/domain/Segment/selectors/selectSegmentVisibility';
import { togglePreferenceId } from '@/creditflowresearch/application/helpers/togglePreferenceId';
import { FieldsNames, IBasePreferenceItem } from './types';

type TPreferences = Partial<IUserPlanPreferences>;

const getSelection = (preferences: TPreferences): IMarketSegmentSelection =>
  preferences[FieldsNames.Segments] ?? EMPTY_MARKET_SEGMENT_SELECTION;

const isHeldByAnyScope = (preferences: TPreferences, segmentId: string): boolean =>
  getSelection(preferences).groups.some(({ marketSegmentIds }) => marketSegmentIds.includes(segmentId));

const toSegmentPreferenceItem = (scopeId: string | undefined, id: string): IBasePreferenceItem => ({
  id,
  scopeId,
  fieldName: FieldsNames.Segments,
  getEntity: () => {
    const abbreviation = SegmentTypeById[id] ?? '';

    return {
      getAbbreviation: () => abbreviation,
      getDisplayName: () => abbreviation,
      canAccess: () => true,
    };
  },
});

export const isSegmentPreferenceItem = (item: IBasePreferenceItem): boolean =>
  item.fieldName === FieldsNames.Segments;

export const getScopeSegmentIds = (preferences: TPreferences, scopeId: string): string[] =>
  getSelection(preferences).groups.find(({ id }) => id === scopeId)?.marketSegmentIds ?? [];

/** A merged item has no row of its own, so it reads as active when any row holds it. */
export const isSegmentActive = (
  preferences: TPreferences,
  item: IBasePreferenceItem,
): boolean => (item.scopeId
  ? getScopeSegmentIds(preferences, item.scopeId).includes(item.id)
  : isHeldByAnyScope(preferences, item.id));

export const getSegmentPreferenceItems = (
  scope: ISegmentScope,
  marketIds: string[] = [],
): IBasePreferenceItem[] => getVisibleSegmentIds(scope, marketIds)
  .map((id) => toSegmentPreferenceItem(scope.id, id));

/** The header dropdown's single row: what every selected row offers, deduplicated and unscoped. */
export const getMergedSegmentItems = (preferences: TPreferences): IBasePreferenceItem[] => {
  const visible = new Set(getSegmentScopes(preferences)
    .flatMap((scope) => getVisibleSegmentIds(scope, preferences.marketIds)));

  return ALL_SEGMENT_IDS.filter((id) => visible.has(id)).map((id) => toSegmentPreferenceItem(undefined, id));
};

const toMarketKey = (marketIds: string[] = []): string => [...marketIds].sort().join(',');

export const withResolvedSegments = <T extends TPreferences>(
  next: T,
  previous?: TPreferences,
): T => {
  const keepsMarkets = previous === undefined
    || toMarketKey(next.marketIds) === toMarketKey(previous.marketIds);

  const groups = getSegmentScopes(next).map((scope) => {
    const visible = getVisibleSegmentIds(scope, next.marketIds);
    const own = getScopeSegmentIds(next, scope.id);
    const saved = own.length ? own : getScopeSegmentIds(previous ?? {}, scope.id);
    const kept = keepsMarkets ? visible.filter((id) => saved.includes(id)) : [];

    return {
      id: scope.id,
      type: scope.type,
      marketSegmentIds: kept.length ? kept : getDefaultSegmentIds(scope, next.marketIds),
    };
  });

  return groups.length
    ? { ...next, [FieldsNames.Segments]: { groups } }
    : omit(next, FieldsNames.Segments) as T;
};

export const toggleSegmentPreference = <T extends TPreferences>(
  preferences: T,
  item: IBasePreferenceItem,
): T => {
  const targets = getSegmentScopes(preferences)
    .filter(({ id }) => !item.scopeId || id === item.scopeId)
    .filter((scope) => getVisibleSegmentIds(scope, preferences.marketIds).includes(item.id));

  if (!targets.length) {
    return preferences;
  }

  const targetIds = new Set(targets.map(({ id }) => id));
  const shouldHold = item.scopeId ? null : !isHeldByAnyScope(preferences, item.id);

  return {
    ...preferences,
    [FieldsNames.Segments]: {
      groups: getSelection(preferences).groups.map((group) => {
        if (!targetIds.has(group.id) || shouldHold === group.marketSegmentIds.includes(item.id)) {
          return group;
        }

        return { ...group, marketSegmentIds: togglePreferenceId(item.id, group.marketSegmentIds) };
      }),
    },
  };
};
